'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface ScheduleItem {
  id: string;
  date: string;
  day_label: string;
  time_start: string;
  time_end: string | null;
  title: string;
  branch: string;
  location: string | null;
  owner_name: string | null;
  status: string;
  has_conflict: boolean;
  missing_logistics: string[];
  room_id: string | null;
  is_recurring?: boolean;
  recurrence_rule?: string | null;
  recurrence_parent_id?: string | null;
}

export interface ConflictInfo {
  itemA: ScheduleItem;
  itemB: ScheduleItem;
  reason: string;
}

function parseTime(timeStr: string): number {
  const [time, period] = timeStr.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  let h = hours;
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return h * 60 + (minutes || 0);
}

function detectConflicts(items: ScheduleItem[]): ConflictInfo[] {
  const conflicts: ConflictInfo[] = [];
  const byOwner: Record<string, ScheduleItem[]> = {};

  items.forEach((item) => {
    if (!item.owner_name) return;
    if (!byOwner[item.owner_name]) byOwner[item.owner_name] = [];
    byOwner[item.owner_name].push(item);
  });

  Object.entries(byOwner).forEach(([owner, ownerItems]) => {
    const byDate: Record<string, ScheduleItem[]> = {};
    ownerItems.forEach((item) => {
      if (!byDate[item.date]) byDate[item.date] = [];
      byDate[item.date].push(item);
    });

    Object.entries(byDate).forEach(([date, dateItems]) => {
      for (let i = 0; i < dateItems.length; i++) {
        for (let j = i + 1; j < dateItems.length; j++) {
          const a = dateItems[i];
          const b = dateItems[j];
          if (!a.time_end || !b.time_end) continue;

          const aStart = parseTime(a.time_start);
          const aEnd = parseTime(a.time_end);
          const bStart = parseTime(b.time_start);
          const bEnd = parseTime(b.time_end);

          if (aStart < bEnd && aEnd > bStart) {
            conflicts.push({
              itemA: a,
              itemB: b,
              reason: `${owner} is double-booked on ${date}`,
            });
          }
        }
      }
    });
  });

  return conflicts;
}

export function useScheduleItems() {
  const [items, setItems] = useState<ScheduleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
  const supabase = createClient();

  const fetchItems = useCallback(async () => {
    const { data } = await supabase
      .from('schedule_items')
      .select('*')
      .order('date', { ascending: true })
      .order('time_start', { ascending: true });

    const loaded = data ?? [];
    setItems(loaded);
    setConflicts(detectConflicts(loaded));
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, loading, conflicts, refetch: fetchItems };
}
